// import { STAGE } from '@/constants';
import { BroadcastChannel, createLeaderElection } from 'broadcast-channel';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { useEffect, useRef } from 'react';

interface UseSSEBroadcastProps<T> {
  url: string | null;
  onError?: (event: Event) => void;
  onMessage?: (data: unknown) => void;
  onOpen?: (event: Event) => void;
  channel?: string;
  listeners?: Record<string, (data: T) => void>;
}

const isDevelopment = true;

/**
 * Hook to create a server-sent event connection and broadcast messages across tabs
 */
export function useSSEBroadcast<T = unknown>({
  url,
  onError,
  onMessage,
  onOpen,
  channel = 'Coinsfly',
  listeners = {},
}: UseSSEBroadcastProps<T>) {
  const eventSource = useRef<EventSource | null>(null);
  const broadcast = useRef(new BroadcastChannel(channel));
  const isLeader = useRef(false);

  useEffect(() => {
    const broadcastChannel = new BroadcastChannel(channel);
    const elector = createLeaderElection(broadcastChannel);

    elector.awaitLeadership().then(() => {
      if (isDevelopment) {
        console.log('I am the leader tab for', channel, eventSource.current);
      }
      if (!eventSource.current) {
        createChannels();
      }

      isLeader.current = true;
      broadcast.current = broadcastChannel;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  useEffect(() => {
    broadcast.current.onmessage = e => {
      if (isDevelopment) {
        console.log('broadcast', e.data, listeners[e.data.event]);
      }

      if (onMessage) {
        onMessage(e.data);
      }

      listeners[e.data.event]?.(e.data.data);
    };
  }, [listeners, onMessage]);

  useEffect(() => {
    if (eventSource.current) {
      eventSource.current.close();

      if (isDevelopment) {
        console.log('Closed event source');
      }
    }

    if (url && isLeader.current) {
      createChannels();

      if (isDevelopment) {
        console.log('Recreated event source for', url);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => {
    const cleanup = () => {
      eventSource.current?.close();
    };

    window.addEventListener('beforeunload', cleanup);

    return () => {
      window.removeEventListener('beforeunload', cleanup);
    };
  }, []);

  const createChannels = () => {
    if (!url) return;
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    eventSource.current = new EventSourcePolyfill(url, {
      withCredentials: true,
      headers: {
        Authorization: accessToken,
      },
    });
    if (isDevelopment) {
      console.log('eventSource', eventSource.current);
    }

    eventSource.current.onopen = e => {
      if (isDevelopment) {
        console.log('Connected to event source at', url);
      }
      if (onOpen) {
        onOpen(e);
      }
    };

    Object.entries(listeners).forEach(([event]) => {
      eventSource.current?.addEventListener(event, e => {
        if (isDevelopment) {
          console.log(event, e.data);
        }
        broadcast.current.postMessage({
          event,
          data: e.data ? JSON.parse(e.data) : undefined,
        });
      });
    });

    eventSource.current.onmessage = e => {
      if (isDevelopment) {
        console.log('SSE Message Event', 'event', e.data);
      }
      broadcast.current.postMessage({
        data: e.data ? JSON.parse(e.data) : undefined,
      });
    };

    eventSource.current.onerror = function (e) {
      if (isDevelopment) {
        console.log('error', e, eventSource.current);
      }

      if (eventSource.current?.readyState === EventSource.CLOSED) {
        if (isDevelopment) {
          console.log('Reconnecting to event source at', url);
        }
        createChannels();
        return;
      }

      if (onError) {
        onError(e);
      }
    };
  };
  //   broadcast.current.onmessage = e => {
}
